import React, { useState, useEffect, useMemo } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import {
    FaPlay, FaPause, FaStop, FaPlayCircle,
    FaSpinner, FaClock, FaListAlt, FaExclamationCircle
} from 'react-icons/fa';
import './ControlePonto.css'; // Vamos criar este arquivo a seguir

// Componente principal do Controle de Ponto
export default function ControlePonto({ user }) {
    // Estado para o relógio digital (atualizado a cada segundo)
    const [currentTime, setCurrentTime] = useState(new Date());

    // Estado para os dados do ponto
    const [marcacoesHoje, setMarcacoesHoje] = useState([]);
    const [statusPonto, setStatusPonto] = useState('fora'); // 'fora', 'trabalhando', 'pausa'
    const [loading, setLoading] = useState(true); // Loading da página
    const [loadingAction, setLoadingAction] = useState(false); // Loading dos botões
    const [error, setError] = useState(null);

    // Efeito para o relógio digital (tick a cada segundo)
    useEffect(() => {
        const timerId = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);

        // Limpa o intervalo quando o componente é desmontado
        return () => {
            clearInterval(timerId);
        };
    }, []); // Roda apenas uma vez na montagem

    // Efeito para buscar os dados do dia
    useEffect(() => {
        const fetchDadosPonto = async () => {
            setLoading(true);
            setError(null);
            try {
                // Rota da API que busca os registros de hoje e o status atual
                // O backend deve retornar: { marcacoes: [...], status: 'trabalhando' }
                const response = await api.get('/api/ponto/hoje');
                
                setMarcacoesHoje(Array.isArray(response.data.marcacoes) ? response.data.marcacoes : []);
                setStatusPonto(response.data.status || 'fora');

            } catch (err) {
                console.error("Erro ao buscar dados do ponto:", err);
                setError("Não foi possível carregar seus registros de ponto. Tente novamente.");
                toast.error("Falha ao carregar dados do ponto.");
            } finally {
                setLoading(false);
            }
        };

        if (user && user.id) {
            fetchDadosPonto();
        }
    }, [user]); // Depende do 'user' estar carregado

    // Função para registrar um novo ponto
    const handleRegistrarPonto = async (tipo) => {
        setLoadingAction(true);
        setError(null);
        
        try {
            // Rota da API que registra o ponto
            const response = await api.post('/api/ponto/registrar', { tipo });

            const { novaMarcacao, novoStatus } = response.data;

            // Atualiza o estado local
            setMarcacoesHoje(prev => [...prev, novaMarcacao]);
            setStatusPonto(novoStatus);

            toast.success(`Registro de '${formatarTipo(tipo)}' salvo com sucesso!`);

        } catch (err) {
            console.error(`Erro ao registrar ${tipo}:`, err);
            const errorMsg = err.response?.data?.error || `Falha ao registrar ${tipo}.`;
            setError(errorMsg);
            toast.error(errorMsg);
        } finally {
            setLoadingAction(false);
        }
    };

    // Formata a hora para o relógio digital (HH:MM:SS)
    const formatarTempo = (date) => {
        const horas = String(date.getHours()).padStart(2, '0');
        const minutos = String(date.getMinutes()).padStart(2, '0');
        const segundos = String(date.getSeconds()).padStart(2, '0');
        return { horas, minutos, segundos };
    };

    const { horas, minutos, segundos } = formatarTempo(currentTime);

    // Formata o tipo de marcação para exibição na lista
    const formatarTipo = (tipo) => {
        switch (tipo) {
            case 'entrada': return 'Entrada';
            case 'saida_pausa': return 'Início Pausa';
            case 'retorno_pausa': return 'Fim Pausa';
            case 'saida': return 'Saída';
            default: return tipo;
        }
    };

    // Lógica para desabilitar os botões com base no status
    // Usamos useMemo para otimizar e não recalcular a cada segundo
    const buttonState = useMemo(() => {
        const isLoading = loading || loadingAction;
        return {
            entrada: isLoading || statusPonto !== 'fora',
            pausa: isLoading || statusPonto !== 'trabalhando',
            retorno: isLoading || statusPonto !== 'pausa',
            saida: isLoading || statusPonto !== 'trabalhando'
        };
    }, [statusPonto, loading, loadingAction]);


    // Renderização do Loading inicial
    if (loading) {
        return (
            <div className="ponto-container loading">
                <FaSpinner className="icon-spin" size={40} />
                <p>Carregando seu espelho de ponto...</p>
            </div>
        );
    }

    return (
        <div className="ponto-container">
            <h1 className="ponto-header"><FaClock /> Registro de Ponto</h1>

            {/* O Relógio Digital */}
            <div className="ponto-relogio-wrapper">
                <div className="ponto-relogio-digital">
                    <span>{horas}</span>:<span>{minutos}</span>:
                    <span className="relogio-segundos">{segundos}</span>
                </div>
                <div className="relogio-data">
                    {currentTime.toLocaleDateString('pt-BR', { dateStyle: 'full' })}
                </div>
            </div>

            {/* Mensagem de Erro */}
            {error && (
                <div className="ponto-error-bar">
                    <FaExclamationCircle /> {error}
                </div>
            )}

            {/* Botões de Ação */}
            <div className="ponto-botoes-grid">
                <button
                    className="ponto-botao btn-entrada"
                    onClick={() => handleRegistrarPonto('entrada')}
                    disabled={buttonState.entrada}
                >
                    {loadingAction ? <FaSpinner className="icon-spin" /> : <FaPlay />}
                    Registrar Entrada
                </button>
                <button
                    className="ponto-botao btn-pausa"
                    onClick={() => handleRegistrarPonto('saida_pausa')}
                    disabled={buttonState.pausa}
                >
                    {loadingAction ? <FaSpinner className="icon-spin" /> : <FaPause />}
                    Sair para Pausa
                </button>
                <button
                    className="ponto-botao btn-retorno"
                    onClick={() => handleRegistrarPonto('retorno_pausa')}
                    disabled={buttonState.retorno}
                >
                    {loadingAction ? <FaSpinner className="icon-spin" /> : <FaPlayCircle />}
                    Retornar da Pausa
                </button>
                <button
                    className="ponto-botao btn-saida"
                    onClick={() => handleRegistrarPonto('saida')}
                    disabled={buttonState.saida}
                >
                    {loadingAction ? <FaSpinner className="icon-spin" /> : <FaStop />}
                    Registrar Saída
                </button>
            </div>

            {/* Lista de Registros do Dia */}
            <div className="ponto-marcacoes-wrapper">
                <h2><FaListAlt /> Marcações de Hoje</h2>
                {marcacoesHoje.length > 0 ? (
                    <ul className="ponto-marcacoes-lista">
                        {marcacoesHoje.map((marcacao) => (
                            <li key={marcacao.id || marcacao.hora} className="ponto-marcacao-item">
                                <span className={`marcacao-tipo tipo-${marcacao.tipo}`}>
                                    {formatarTipo(marcacao.tipo)}
                                </span>
                                <span className="marcacao-hora">
                                    {/* Formata a hora vinda do DB (ex: "08:00:15") */}
                                    {marcacao.hora.split(':').slice(0, 3).join(':')}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="sem-marcacoes">Nenhum registro encontrado para hoje.</p>
                )}
            </div>
        </div>
    );
}